import { useCallback, useEffect, useState } from "react";
import moment from "moment";

import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { ComboBoxControl } from "@/Common/Components/Inputs/ComboboxControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { RadioButton } from "@/Common/Components/Inputs/RadioButton";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ConsentPrograms, ConsentProgramsData } from "@/Common/Models/ConsentPrograms";
import { Person } from "@/Common/Models/Person/Person";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { ConsentStatus, Respondent } from "@/Patient/Models/Consent/ConsentEnum";
import { RelationView } from "../Profile/Relations/RelationView";
import { ConsentAction } from "./Actions/ConsentAction";

import styles from "./ConsentProgramDialog.module.scss";

interface IConsentProgramDialogProps {
    onSave: (status: ConsentStatus) => void;
    onAccessWithoutConsent?: (programId: number) => void;
    person: Person;
    onClose: () => void;
    code?: string;
    showAccessWithoutConsent?: boolean;
    defaultOption?: ConsentStatus;
}

function ConsentProgramDialog({ onSave, onClose, code, onAccessWithoutConsent, showAccessWithoutConsent, person, defaultOption }: IConsentProgramDialogProps): JSX.Element {
    const { getText } = useTranslation();
    const [selectedRespondent, setSelectedRespondent] = useState(Respondent.Patient);
    const [currentDateTime, setSelectedDate] = useState(moment().toDate());
    const [selectedConsentOption, setSelectedConsentOption] = useState(defaultOption);
    const [consentProgram, setConsentProgram] = useState(Immutable.List<ConsentPrograms>());
    const [selectedProgramId, setSelectedProgramId] = useState(0);
    let representative: LinkedContact;
    const [selectedRepresentative, setSelectedRepresentative] = useState(representative);
    let representativeList = person.Relationships?.filter(p => p.IsRepresentative);

    const onChangeConsentProgram = (e: any): void => {
        setSelectedProgramId(e);
    };

    const onSelectRespondent = (respondent: Respondent): void => {
        setSelectedRespondent(respondent);
    };

    const onSaveConsent = useCallback(async () => {
        const action = new ConsentAction();
        const givenBy = selectedRespondent === Respondent.Patient ? person.ContactId : selectedRepresentative.TargetContactId;
        const response = await action.saveConsent(person.ContactId, selectedProgramId, givenBy, currentDateTime, selectedConsentOption);
        if (response != null) {
            onSave(selectedConsentOption);
        }
    }, [onSave, selectedProgramId, currentDateTime, selectedConsentOption, selectedRepresentative, person, selectedRespondent]);

    const onRepresentativeSelect = (selectedRepresentative: LinkedContact): void => {
        setSelectedRepresentative(selectedRepresentative);
    };

    const consentPrograms = useCallback(async () => {
        const action = new ConsentAction();
        const response = await action.getConsentPrograms();
        if (response != null) {
            let consentProgramsResponse: ConsentProgramsData[] = response.ConsentPrograms;
            let consentPrograms = consentProgramsResponse.map(c => new ConsentPrograms(c));
            setConsentProgram(consentPrograms.toImmutable());
            if (!IsNullOrWhiteSpace(code)) {
                let selectedProgram = consentPrograms.find(x => x.model.Code == code).model.Id;
                setSelectedProgramId(selectedProgram);
            }
        }
    }, [code]);

    useEffect(() => {
        consentPrograms();
    }, [consentPrograms]);

    const patientRepresentative = React.useMemo(() => representativeList.map(representative => {
        const isSelected = selectedRepresentative && (
            representative === selectedRepresentative
        );
        return (
            <div key={representative.Id} className={`${isSelected ? styles.selected : ""} ${styles.relationView}`} onClick={() => onRepresentativeSelect(representative)}>
                <RelationView
                    linkedContact={representative}
                    hidePermissions
                    isReadOnly
                />
            </div>
        );
    }), [representativeList, selectedRepresentative]);

    const isSaveButtonDisabled = React.useMemo(() => (
        selectedProgramId == 0 ||
        selectedConsentOption == null ||
        (selectedRespondent == Respondent.PatientRepresentative && patientRepresentative.length == 0) ||
        (selectedRespondent == Respondent.PatientRepresentative && selectedRepresentative == null) ||
        currentDateTime == null
    ), [patientRepresentative, selectedConsentOption, selectedRespondent, currentDateTime, selectedRepresentative, selectedProgramId]);

    const footer = React.useMemo(() => (
        <div className="p-footer">
            <div className="text-right">
                <button className="btn btn-default push-right-5" onClick={() => onClose()}>{getText("CancelLabel")}</button>
                {showAccessWithoutConsent && (
                    <button className="btn btn-danger push-right-5" onClick={() => onAccessWithoutConsent(selectedProgramId)}>{getText("AccessWithoutConsentLabel")}</button>
                )}
                <button
                    className="btn btn-primary"
                    disabled={isSaveButtonDisabled}
                    onClick={() => onSaveConsent()}
                >
                    {getText("SaveLabel")}
                </button>
            </div>
        </div>
    ), [onClose, onAccessWithoutConsent, getText, showAccessWithoutConsent, onSaveConsent, isSaveButtonDisabled, selectedProgramId]);

    return (
        <Modal
            showX
            centerContent
            title={getText("ConsentProgramLabel")}
            onClose={onClose}
            footer={footer}
            disableOverlayClose
            maxHeight="600px"
            width={700}
            showOverlay
            fixedOverlayPosition
        >
            <div className="p-body">
                <div className={styles.section}>
                    <div className={styles.label}>
                        <strong>{getText("ProgramLabel")}: </strong>
                    </div>
                    <ComboBoxControl
                        id="consentProgramsField"
                        hasEmptyItem
                        items={Immutable.List(consentProgram.map(x => ConsentPrograms.toComboboxItem(x)))}
                        selectedCode={selectedProgramId.toString()}
                        disabled={!IsNullOrWhiteSpace(code)}
                        onChange={(selectedCode: string) => onChangeConsentProgram(selectedCode)}
                        className={styles.combo}
                    />
                </div>
                <div className={styles.section}>
                    <p>{getText("ConsentToPermitToConsultPastMedicalFilesLabel")}</p>
                </div>
                <div className={styles.section}>
                    <p>{getText("ConsentToTransmission")}</p>
                </div>
                <div className={styles.section}>
                    <p>{getText("ConsentMayBeRovoked")}</p>
                </div>
                <div className={styles.section}>
                    <div className={styles.label}>
                        <strong>{getText("RespondentLabel")}: </strong>
                    </div>
                    <div className={styles.radio}>
                        <RadioButton
                            mainDivCSS=""
                            items={Immutable.List([
                                new KeyValuePair(getText("PatientConsentLabel"), Respondent.Patient),
                                new KeyValuePair(getText("PatientRepresentativeLabel"), Respondent.PatientRepresentative)
                            ])}
                            radioInputGroupId="respondent"
                            onePerLine={false}
                            changeOperation={null}
                            onChange={item => onSelectRespondent(item.Value)}
                            selectedItem={selectedRespondent}
                        />
                    </div>
                </div>
                <div className={styles.section}>
                    <div className={styles.label}>
                        <strong>{getText("ApplicableFromLabel")}: </strong>
                    </div>
                    <div className="flex-container">
                        <DateSelector
                            id="_dateConsentedOrRefused"
                            date={currentDateTime}
                            onChangeCallback={(e: Date) => setSelectedDate(e)}
                            dateFormat="yyyy-MM-dd"
                            toDayEnabled={true}
                            maxDate={moment().toDate()}
                            width={150}
                        />
                    </div>
                </div>
                {representativeList.length > 0 && selectedRespondent === Respondent.PatientRepresentative && (
                    <div>
                        <div className={styles.label}>
                            <strong>{getText("PatientRepresentativesLabel")}: </strong>
                        </div>
                        <div className={styles.label}>
                            {patientRepresentative}
                        </div>
                    </div>
                )}
                {representativeList.length === 0 && selectedRespondent === Respondent.PatientRepresentative && (
                    <div className={styles.label}>
                        <p>{getText("NoPatientRepresentativesMessage")}</p>
                    </div>
                )}
                <div>
                    <div className={styles.label}>
                        <strong>{getText("ChooseAnOptionBelowLabel")}: </strong>
                    </div>
                    <div className={styles.element}>
                        <RadioButton
                            mainDivCSS=""
                            items={Immutable.List([
                                new KeyValuePair(getText("ConsentedLabel"), ConsentStatus.Consented),
                                new KeyValuePair(getText("RefusedOrRevokedLabel"), ConsentStatus.Refused),
                                new KeyValuePair(getText("DidntRespondLabel"), ConsentStatus.NoResponse)
                            ])}
                            radioInputGroupId="consentOptions"
                            onePerLine
                            onChange={item => setSelectedConsentOption(item.Value)}
                            selectedItem={selectedConsentOption}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export { ConsentProgramDialog };
